/* The signature visual at the top of the capability panel.

   Row archetypes alone weren't enough — ten capabilities still read as "a list
   with different words". Each one now leads with its own chart: a distribution
   for scoring, a day heat-strip for hourly, a funnel for CRM, a ring for goals.
   That's what makes the panel look like a different screen when the chip row
   advances.

   Pure presentation, no state: React owns this block, the live simulation owns
   the rows underneath it. */

function Flow({ c }) {
  /* five teams feeding one brain — the org map in miniature */
  return (
    <svg className="ch-art" viewBox="0 0 120 44" aria-hidden="true">
      {[6, 17, 28, 39, 50].map((y, i) => (
        <g key={y}>
          <rect x="2" y={y - 4} width="16" height="8" rx="2.5" fill={c} opacity={0.18 + i * 0.06} />
          <path d={`M19 ${y} C 46 ${y}, 62 22, 88 22`} fill="none" stroke={c} strokeWidth="1.1" opacity=".38" strokeDasharray="2.5 3" />
        </g>
      ))}
      <circle cx="95" cy="22" r="9" fill="#fff" stroke={c} strokeWidth="1.6" />
      <circle cx="95" cy="22" r="3.4" fill={c} />
    </svg>
  );
}

function Typing({ c }) {
  return (
    <span className="ch-typing" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <i key={i} style={{ background: c, animationDelay: `${i * 160}ms` }} />
      ))}
      <span className="ch-typing-line" style={{ background: `${c}22` }} />
    </span>
  );
}

function Dist({ c, values = [] }) {
  const max = Math.max(...values, 1);
  return (
    <span className="ch-dist" aria-hidden="true">
      {values.map((v, i) => (
        <i key={i} style={{ height: `${(v / max) * 100}%`, background: i === 2 ? c : `${c}55` }} />
      ))}
    </span>
  );
}

function Page({ c }) {
  return (
    <span className="ch-page" aria-hidden="true">
      {[100, 82, 92, 61, 74].map((w, i) => (
        <i key={i} style={{ width: `${w}%`, background: i === 0 ? c : `${c}33` }} />
      ))}
    </span>
  );
}

function Day({ c, values = [] }) {
  return (
    <span className="ch-day" aria-hidden="true">
      {values.map((v, i) => (
        <i key={i} style={{ height: `${v * 33}%`, background: v === 3 ? c : v === 2 ? `${c}88` : `${c}30` }} />
      ))}
    </span>
  );
}

function Wait({ c, values = [] }) {
  const max = Math.max(...values, 1);
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * 118 + 1},${40 - (v / max) * 34}`)
    .join(' ');
  return (
    <svg className="ch-art" viewBox="0 0 120 44" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`${pts} 119,43 1,43`} fill={c} opacity=".10" stroke="none" />
      <circle cx="119" cy={40 - (values[values.length - 1] / max) * 34} r="3" fill={c} />
    </svg>
  );
}

function Ring({ c, pct = 0 }) {
  const r = 17;
  const circ = 2 * Math.PI * r;
  return (
    <svg className="ch-art ch-ring" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r={r} fill="none" stroke="#eef0f2" strokeWidth="5" />
      <circle
        cx="22" cy="22" r={r} fill="none" stroke={c} strokeWidth="5" strokeLinecap="round"
        strokeDasharray={`${(pct / 100) * circ} ${circ}`} transform="rotate(-90 22 22)"
      />
    </svg>
  );
}

function Split({ values = [] }) {
  return (
    <span className="ch-split" aria-hidden="true">
      {values.map(([w, col], i) => (
        <i key={i} style={{ width: `${w}%`, background: col }} />
      ))}
    </span>
  );
}

function Funnel({ c, values = [] }) {
  return (
    <span className="ch-funnel" aria-hidden="true">
      {values.map((w, i) => (
        <i key={i} style={{ width: `${w}%`, background: c, opacity: 1 - i * 0.19 }} />
      ))}
    </span>
  );
}

function Week({ c, values = [] }) {
  return (
    <span className="ch-week" aria-hidden="true">
      {values.map((v, i) => (
        <i key={i} style={{ background: v ? c : '#e6e8ec', opacity: v ? 0.35 + (i % 5) * 0.16 : 1 }} />
      ))}
    </span>
  );
}

const ART = { flow: Flow, typing: Typing, dist: Dist, page: Page, day: Day, wait: Wait, ring: Ring, split: Split, funnel: Funnel, week: Week };

export default function CapabilityHead({ head }) {
  if (!head) return null;
  const Art = ART[head.kind] || Flow;
  return (
    <div className="ch" key={head.kind}>
      <div className="ch-nums">
        <span className="ch-stat" style={{ color: head.c }}>{head.stat}</span>
        <span className="ch-unit">{head.unit}</span>
        <span className="ch-note">{head.note}</span>
      </div>
      <div className="ch-vis">
        <Art {...head} />
      </div>
    </div>
  );
}
